import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { isNil, last, nth, orderBy, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { Level, Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	DoorstroomMeasure,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { attrLabel } from '../../services/labels';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { maxSumOfStacksIf, MultiAggregator, noAgg0, sumIf, sumOver } from '../../services/aggregation';
import { att, att0, count_records, percOfRow } from '../../services/measures';
import { generateCssClassForAttrPath } from '../../services/css-generate-metadata';
import { deelVeilig, generateCssClassForString } from '@cumlaude/shared-utils';
import { BarInfo } from '../../services/stacked-bars';
import { DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { defaultDoorstroomActueelFilters, defaultDoorstroomHistorieFilters } from '../../services/filter-config';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { DashboardVariant, Eenheid, KENMERKEN } from '../../services/weergave-opties';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { AttributeSelectorComponent } from '../../attribute-selector/attribute-selector.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { Axis, createYAxis } from '../../services/axis';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface KenmerkI extends Attributes {
	[kenmerkNaam: string]: string | number | null;
	ds_m_nr_weging: number;
	ds_m_nr_leerlingen: number;
}

interface KenmerkA extends Attributes {
	ds_m_nr_weging: number;
	max: number;
	wegingMetKenmerk: number;
	leerlingenMetKenmerk: number;
	ds_m_nr_leerlingen: number;
}

@Component({
	selector: 'app-kenmerk',
	templateUrl: './kenmerk.component.html',
	styleUrls: ['./kenmerk.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		AttributeSelectorComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		FilterBarComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KenmerkComponent extends BarchartTableConfig<KenmerkI, KenmerkA> {
	private readonly dataService = inject(DataService);

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	historieSubgroups = computed(() => [
		...this.groeperingService.huidigeGroeperingen().slice(-1),
		['ds_nm_schooljaar_van'] as AttrPath,
		this.kenmerk(),
	]);

	override availableGroups = defaultDoorstroomAvailableGroups;

	actueelFilters: FilterName[] = [...defaultDoorstroomActueelFilters, 'x_peildatum'];

	historieFilters: FilterName[] = [...defaultDoorstroomHistorieFilters, 'x_peildatum'];

	kenmerken = KENMERKEN;

	kenmerk = this.qp.signal('kenmerk');

	eenheid = this.qp.signal('eenheid');

	variant = this.qp.signal('variant');

	permanentFilterExpressions = [
		// Voorkom dubbelingen door doorstroom-record van relevante plaatsing naar zowel niet- als wel-relevante plaatsing
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
	];

	partitionMeasure = computed<PartitionMeasure<KenmerkA>>(() => {
		if (this.eenheid() === Eenheid.PERCENTAGE) {
			return {
				type: 'percentage',
				getValue: percOfRow<keyof KenmerkA, KenmerkA>('wegingMetKenmerk', 'ds_m_nr_weging'),
			};
		}

		return {
			type: 'number',
			getValue: att('wegingMetKenmerk'),
		};
	});

	legendaIncludeNull = computed(() => kenmerkMultiple(this.kenmerk().join('.')));

	factTable = Table.fac_ds_doorstroom;

	constructor() {
		super();

		effect(() => {
			this.eenheid();
			this.filterService.refresh();
		});
	}

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({ ...options, m: [DoorstroomMeasure.LEERLINGEN], r: range(0, this.getRowLevel() + 1) });
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	protected override singleAggregators = {
		wegingMetKenmerk: sumIf<'ds_m_nr_weging', KenmerkI>('ds_m_nr_weging', ({ [this.kenmerk().join('.')]: kenmerkWaarde }: KenmerkI) =>
			kenmerkGevuld(<string>kenmerkWaarde)
		),
		leerlingenMetKenmerk: sumIf<'count_records', KenmerkI>('count_records', ({ [this.kenmerk().join('.')]: kenmerkWaarde }: KenmerkI) =>
			kenmerkGevuld(<string>kenmerkWaarde)
		),
		ds_m_nr_weging: sumOver<'ds_m_nr_weging', KenmerkI, number>('ds_m_nr_weging'),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', KenmerkI>('ds_m_nr_leerlingen'),
	};

	protected override multiAggregators: MultiAggregator<keyof KenmerkA, KenmerkI, KenmerkA, number>[] = [
		maxSumOfStacksIf<'max', 'ds_m_nr_weging', KenmerkA, KenmerkI>(
			'max',
			'ds_m_nr_weging',
			() => this.groeperingService.huidigeAantalGroeperingen(),
			({ [this.kenmerk().join('.')]: kenmerkWaarde }: KenmerkI) => kenmerkGevuld(<string>kenmerkWaarde)
		),
	];

	override partitionBarData(rowRoot: Level<KenmerkA, number[]>): Path<KenmerkA, number[]>[][] {
		switch (this.eenheid()) {
			case Eenheid.AANTAL:
				return [rowRoot.r.filter((path) => kenmerkGevuld(last(path)!.k))];
			case Eenheid.PERCENTAGE:
				// Eerst de bars met gevulde kenmerkwaarden, daarna zonder
				return [orderBy(rowRoot.r, [(path) => kenmerkGevuld(last(path)!.k)], ['desc'])];
		}
	}

	override makeBar(attrs: KenmerkI, path: Path<KenmerkA, number[]>, context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>): BarInfo {
		const kenmerkNaam = this.kenmerk();
		const kenmerkString = kenmerkNaam.join('.');
		const kenmerkWaarde = attrs[kenmerkString];

		const kenmerkLabel = attrLabel(kenmerkNaam);
		const heeftKenmerk = kenmerkGevuld(<string>kenmerkWaarde) || kenmerkWaarde === 'Geen';

		let kenmerkWaardeClass;
		let kenmerkWaardeLabel;
		if (heeftKenmerk) {
			if (kenmerkWaarde == '1') {
				kenmerkWaardeClass = generateCssClassForAttrPath(kenmerkNaam);
				kenmerkWaardeLabel = kenmerkLabel;
			} else {
				kenmerkWaardeClass = generateCssClassForString(kenmerkWaarde!.toString());
				kenmerkWaardeLabel = this.displayService.display(<string | null>kenmerkWaarde);
			}
		} else {
			kenmerkWaardeLabel = 'Geen ' + kenmerkLabel;
			kenmerkWaardeClass = generateCssClassForString(kenmerkWaardeLabel);
		}

		const rowAantal = nth(path, -2)!.a.ds_m_nr_weging;
		const percentage = Math.round(deelVeilig(attrs.ds_m_nr_weging, rowAantal) * 100);

		const tooltipElements: TooltipElement[] = [
			{ label: `Aantal:`, value: `${formatDecimal(attrs.ds_m_nr_weging, '1.0-2')}` },
			{ label: `Percentage:`, value: `${percentage}%` },
			{ label: `${kenmerkMultiple(kenmerkString) ? kenmerkLabel : 'Kenmerk'}:`, value: `${kenmerkWaardeLabel}` },
		];
		const description = kenmerkMultiple(kenmerkString) && heeftKenmerk ? `${kenmerkLabel}: ${kenmerkWaardeLabel}` : kenmerkWaardeLabel;

		const size = context.config.eenheid() === Eenheid.PERCENTAGE ? percentage : attrs.ds_m_nr_weging;

		return {
			...super.makeBar(attrs, path, context),
			size,
			description,
			className: kenmerkWaardeClass,
			tooltip: tooltipElements,
		};
	}

	getKenmerkClass(): string {
		const kenmerkLabel = attrLabel(this.kenmerk());
		return 'kenmerk-' + kenmerkLabel.toLowerCase();
	}

	override createMeasureColumns(context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>): ColumnDef<DataRow<KenmerkA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		const kenmerkGevuldExtraFilters = [this.getKenmerkGevuldFilter()];

		return [
			this.createLeerlingColumn(
				'Percentage',
				percOfRow('wegingMetKenmerk', 'ds_m_nr_weging'),
				{ context, dataType: 'percentage' },
				att('leerlingenMetKenmerk'),
				kenmerkGevuldExtraFilters
			),
			this.createLeerlingColumn(
				'Aantal',
				att('wegingMetKenmerk'),
				{ context, format: '1.0-2' },
				att('leerlingenMetKenmerk'),
				kenmerkGevuldExtraFilters
			),
			this.createLeerlingColumn('Leerlingen', att('ds_m_nr_weging'), { context }, count_records),
		];
	}

	override getSelectionConfig(context: DataRequestContext<KenmerkA>): SelectionConfig<KenmerkA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			getSize: att('ds_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	override enrichTableModel(_context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>, tableModel: TableModel<DataRow<KenmerkA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	private getKenmerkGevuldFilter(): FilterExpression {
		const kenmerk = this.kenmerk();
		const kenmerkString = kenmerk.join('.');

		let value = undefined;
		if (['ds_fk_ilt_van.ilt_is_lwoo', 'ds_fk_ilt_van.ilt_is_vavo'].includes(kenmerkString)) {
			value = [0];
		} else if (['ds_is_apcg_van', 'ds_is_examenopstroom_van'].includes(kenmerkString)) {
			value = [0, null];
		} else if (kenmerkMultiple(kenmerkString)) {
			value = [null, 'Geen', ''];
		}

		return new BasicFilterExpression(kenmerk, value, 'not in');
	}

	override getDisplayOptions(): ExportFilter[] {
		return [{ label: 'Kenmerk', value: attrLabel(this.kenmerk()) }];
	}

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override createYAxis(context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>): Axis {
		if (!context.dataRoot || context.config.eenheid() === Eenheid.PERCENTAGE) return super.createYAxis(context);
		return createYAxis([0, context.dataRoot.a.max]);
	}

	override createXAxis(context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>): Axis {
		if (!context.dataRoot || context.config.eenheid() === Eenheid.PERCENTAGE) return super.createXAxis(context);
		return { min: 0, max: context.dataRoot.a.max, ticks: [] };
	}

	override getBarchartQty(path: Path<KenmerkA, number[]>, context: DashboardContext<KenmerkI, KenmerkA, KenmerkComponent>) {
		if (context.config.eenheid() === Eenheid.AANTAL) return att0('wegingMetKenmerk')(path);
		else return 100;
	}

	protected readonly DashboardVariant = DashboardVariant;
	protected readonly Eenheid = Eenheid;
}

function kenmerkGevuld(waarde: string | null | undefined): boolean {
	return !isNil(waarde) && !['', '0', 'Geen'].includes(waarde);
}

function kenmerkMultiple(kenmerk: string): boolean {
	return ['ds_nm_instroom_in_schooljaar', 'ds_nm_uitstroom_in_schooljaar'].includes(kenmerk);
}
